import {Component, OnInit} from '@angular/core';
import {User} from '@models/user';
import {EMPTY_USER} from '@models/empty';
import {environment} from '~/environments/environment';
import {AuthenticationService} from '@services/authentification.service';
import {HttpClient} from '@angular/common/http';

interface DailyUsage {
    date: string;
    cost: number;
}

interface CreditResponse {
    plan_credit: number;
    plan_renew_date: string;
    ayg_credit: number;
    ayg_expire_date: string;
    daily_usage: DailyUsage[];
}

interface ApiLog {
    id: number;
    created_at: string;
    url: string;
    params: string;
    query_params: string;
    time: number;
    status_code: number;
    cost: number;
    nb_results: number;
}

@Component({
    selector: 'app-dashboard-api',
    templateUrl: './dashboard-api.component.html',
    styleUrls: ['./dashboard-api.component.scss']
})
export class DashboardApiComponent implements OnInit {
    user: User = Object.assign({}, EMPTY_USER);

    tokens: string[] = [];
    logs: ApiLog[] = [];
    logsLoaded = false;
    loadingLogs = false;
    copiedToken = false;

    creditData: CreditResponse = {
        plan_credit: 0,
        plan_renew_date: '',
        ayg_credit: 0,
        ayg_expire_date: '',
        daily_usage: []
    };

    echartsInstance = null;

    chartOptions = {
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'shadow'
            },
            formatter: '{b}<br/>{a} : <b>{c}</b> crédits'
        },
        grid: {
            left: '12px',
            right: '12px',
            top: '40px',
            bottom: '24px',
            containLabel: true
        },
        legend: {
            data: ['Crédits consommés par jour'],
            top: '4px',
            right: '12px',
            textStyle: {
                fontSize: 12,
                color: '#989B9E'
            }
        },
        xAxis: {
            type: 'category',
            data: [],
            axisTick: {
                alignWithLabel: true,
            },
            axisLabel: {
                interval: 'auto',
                rotate: 0,
                fontSize: 11,
                color: '#989B9E'
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                fontSize: 11,
                color: '#989B9E',
                formatter: (value) => {
                    if (value >= 1000) {
                        return (value / 1000) + 'k';
                    }
                    return value;
                }
            },
            splitLine: {
                lineStyle: {
                    type: 'dashed',
                    color: '#DDDFE1'
                }
            }
        },
        color: ['#FF9500'],
        series: []
    };

    chartData = {};

    constructor(
        public authenticationService: AuthenticationService,
        private http: HttpClient
    ) {
    }

    ngOnInit(): void {
        this.user = this.authenticationService.currentUser;
        this.loadCredits();
        this.loadTokens();
        this.setupLogsToggle();
    }

    loadCredits() {
        this.http.get<CreditResponse>(`${environment.apiUrl}app/user/api/credit`).subscribe(
            data => {
                this.creditData = data;
                this.buildChart(data.daily_usage);
            }
        );
    }

    loadTokens() {
        this.http.get<string[]>(`${environment.apiUrl}app/user/api/token`).subscribe(
            data => {
                this.tokens = data;
            }
        );
    }

    setupLogsToggle() {
        // Observe le clic sur le card-header du bloc logs pour charger les données à l'ouverture
        setTimeout(() => {
            const logsCard = document.querySelector('.logs-block');
            if (logsCard) {
                const header = logsCard.querySelector('.card-header');
                if (header) {
                    header.addEventListener('click', () => {
                        if (!this.logsLoaded) {
                            this.loadLogs();
                        }
                    });
                }
            }
        }, 100);
    }

    loadLogs() {
        if (this.logsLoaded) return;
        this.loadingLogs = true;
        this.logsLoaded = true;
        this.http.get<ApiLog[]>(`${environment.apiUrl}app/user/api/log`).subscribe(
            data => {
                this.logs = data;
                this.loadingLogs = false;
            },
            () => {
                this.loadingLogs = false;
            }
        );
    }

    getExpirationDate(): string {
        if (this.user.api_plan != '' && this.creditData.plan_renew_date != '') {
            return this.formatDate(this.creditData.plan_renew_date);
        }
        if (this.creditData.ayg_credit > 0 && this.creditData.ayg_expire_date != '') {
            return this.formatDate(this.creditData.ayg_expire_date);
        }
        return '';
    }

    buildChart(dailyUsage: DailyUsage[]) {
        const labels = [];
        const values = [];

        dailyUsage.forEach(d => {
            const date = new Date(d.date);
            const day = date.getDate();
            const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
            labels.push(day + ' ' + monthNames[date.getMonth()]);
            values.push(d.cost);
        });

        this.chartOptions.xAxis.data = labels;

        this.chartData = {
            series: [
                {
                    name: 'Crédits consommés par jour',
                    type: 'bar',
                    data: values,
                    barMaxWidth: 20,
                    itemStyle: {
                        borderRadius: [3, 3, 0, 0]
                    }
                }
            ]
        };

        if (this.echartsInstance) {
            this.echartsInstance.setOption(this.chartOptions);
        }
    }

    onChartInit(ec) {
        this.echartsInstance = ec;
    }

    copyToken(token: string) {
        navigator.clipboard.writeText(token).then(() => {
            this.copiedToken = true;
            setTimeout(() => this.copiedToken = false, 2000);
        });
    }

    formatDate(dateStr: string): string {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return d.toLocaleDateString('fr-FR', {day: '2-digit', month: '2-digit', year: 'numeric'});
    }

    getStatusClass(code: number): string {
        if (code >= 200 && code < 300) return 'status-ok';
        if (code >= 400 && code < 500) return 'status-warn';
        return 'status-error';
    }

    getPlanLabel(): string {
        const labels = {
            'api_starter': 'Starter',
            'api_pro': 'Pro',
            'api_business': 'Business',
            'api_enterprise': 'Entreprise',
        };
        return labels[this.user.api_plan] || this.user.api_plan;
    }
}
